/**
 * The check that catches a StockX buy that cost more than the shopper paid (Kai 2026-10-27), the
 * way fanatics-bot skips a Fanatics card above its maximum price. It imports nothing, so the
 * dashboard runs the same comparison the move to PROCESSING runs, and the two cannot disagree.
 */

/** Refused when a StockX paid cost is above what the shopper paid for the line and staff did not confirm it. */
export const PAID_COST_ABOVE_CHARGED = "PAID_COST_ABOVE_CHARGED";

/**
 * The amounts a StockX order details page lists, in USD cents, as staff type them from it (Kai
 * 2026-10-28). A US page shows Your Purchase Price, Sales Tax, Marketplace Fee, Shipping and
 * Total, in that order. Sales Tax is optional because an All-in Ask country may show none, and
 * Additional charges holds any other line a non-US page shows (VAT, duties, import fees).
 */
export interface PaidCostParts {
  purchasePriceCents: number;
  salesTaxCents?: number;
  marketplaceFeeCents: number;
  shippingCents: number;
  additionalChargesCents?: number;
}

/**
 * The item cost of a StockX buy: purchase price, sales tax, marketplace fee and additional
 * charges. Shipping is left out, because the size refund never reads it and a US line's check
 * does not either.
 */
export function itemCostCentsOf(parts: PaidCostParts): number {
  return (
    parts.purchasePriceCents +
    (parts.salesTaxCents ?? 0) +
    parts.marketplaceFeeCents +
    (parts.additionalChargesCents ?? 0)
  );
}

/** The StockX order's Total, the card charge: the item cost plus shipping. */
export function checkoutTotalCentsOf(parts: PaidCostParts): number {
  return itemCostCentsOf(parts) + parts.shippingCents;
}

/** What one line's check reads of the entered paid cost. A single total sent alone is the item cost with no shipping. */
export interface EnteredPaidCost {
  itemCostCents: number;
  shippingCents: number;
}

/** One StockX line whose paid cost is above its limit, in USD cents, as the refusal and the warning show it. */
export interface PaidAboveChargedLine {
  lineId: string;
  chargedPriceCents: number;
  /**
   * The US shipping the shopper paid inside the listing value, counted on a line shipped outside
   * the US; 0 on a US line and on one written before Loot OS recorded it.
   */
  chargedUsShippingCents: number;
  /** What the destination added over US shipping; 0 on a line with none recorded (a US line). */
  chargedShippingSurchargeCents: number;
  itemCostCents: number;
  shippingCents: number;
  /** Whether shipping counts on both sides: the line was shipped outside the US ({@link hasShippingSurcharge}). */
  countsShipping: boolean;
  /** The amount held to the limit: the item cost, plus shipping on a line shipped outside the US. */
  comparedCostCents: number;
  /** The charged price, plus the US shipping and the surcharge on a line shipped outside the US. */
  limitCents: number;
}

/** One line as the check reads it: the snapshot's charged price, US shipping and shipping surcharge. */
export interface ChargedLine {
  id: string;
  chargedPriceCents?: unknown;
  chargedUsShippingCents?: unknown;
  chargedShippingSurchargeCents?: unknown;
}

/**
 * Whether a charged price is one a listing row gives: a positive whole number of cents. A line
 * without one gets no size refund (`stockxLineRefundCents`) and no check here.
 */
export function isUsableChargedPrice(chargedPriceCents: unknown): chargedPriceCents is number {
  return (
    typeof chargedPriceCents === "number" &&
    Number.isInteger(chargedPriceCents) &&
    chargedPriceCents > 0
  );
}

/**
 * Whether a line was shipped outside the US: Loot OS recorded its shipping surcharge, a whole
 * number of cents at least 0, when the line was written. Such a line's check counts shipping on
 * both sides. Every other line (US, no country, or written before the surcharge was recorded)
 * leaves shipping out.
 */
export function hasShippingSurcharge(
  chargedShippingSurchargeCents: unknown,
): chargedShippingSurchargeCents is number {
  return isWholeCentsAtLeastZero(chargedShippingSurchargeCents);
}

function isWholeCentsAtLeastZero(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

/**
 * The lines whose paid cost is above what the shopper paid for the item (Kai 2026-10-28). On a US
 * line the item cost (purchase price, sales tax, marketplace fee, additional charges) is held to
 * the charged price Loot OS stored on the line (`snapshot.chargedPriceCents`, never the tenant's
 * `listingPrice`). On a line shipped outside the US ({@link hasShippingSurcharge}) the item cost
 * plus shipping is held to what the shopper paid: the charged price, plus
 * `snapshot.chargedUsShippingCents`, the US shipping the listing value holds, plus
 * `snapshot.chargedShippingSurchargeCents`, the surcharge that covers the destination's higher ask
 * and its shipping. A line written before the US shipping was recorded (no whole number of cents
 * at least 0) leaves it out, as the limit did then; a US line never reads it. An amount equal to
 * the limit passes. A line with no usable charged price, or with no entered cost in the map, is
 * left out.
 */
export function findPaidAboveCharged(
  lines: readonly ChargedLine[],
  enteredByLineId: ReadonlyMap<string, EnteredPaidCost>,
): PaidAboveChargedLine[] {
  const above: PaidAboveChargedLine[] = [];
  for (const line of lines) {
    const entered = enteredByLineId.get(line.id);
    if (entered === undefined || !isUsableChargedPrice(line.chargedPriceCents)) continue;
    const surcharge = line.chargedShippingSurchargeCents;
    const chargedShippingSurchargeCents = hasShippingSurcharge(surcharge) ? surcharge : null;
    const usShipping = line.chargedUsShippingCents;
    const chargedUsShippingCents =
      chargedShippingSurchargeCents !== null && isWholeCentsAtLeastZero(usShipping)
        ? usShipping
        : 0;
    const comparedCostCents =
      entered.itemCostCents + (chargedShippingSurchargeCents === null ? 0 : entered.shippingCents);
    const limitCents =
      line.chargedPriceCents + chargedUsShippingCents + (chargedShippingSurchargeCents ?? 0);
    if (comparedCostCents > limitCents) {
      above.push({
        lineId: line.id,
        chargedPriceCents: line.chargedPriceCents,
        chargedUsShippingCents,
        chargedShippingSurchargeCents: chargedShippingSurchargeCents ?? 0,
        itemCostCents: entered.itemCostCents,
        shippingCents: entered.shippingCents,
        countsShipping: chargedShippingSurchargeCents !== null,
        comparedCostCents,
        limitCents,
      });
    }
  }
  return above;
}
